import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { MagnifyingGlass, BookmarkSimple, ArrowsDownUp, SlidersHorizontal } from "@phosphor-icons/react";
import { savedSearches, useLocalList, recentlyViewed } from "@/lib/storage";
import { faqSchema, breadcrumbSchema } from "@/lib/schema";
import { toast } from "sonner";
import { cachedGet } from "@/lib/api";

// FAQs surfaced by mode — feeds Google AI Overviews + Perplexity + ChatGPT
// answer boxes with authoritative Point Penta responses to buyer queries.
const MODE_FAQS = {
  buy: [
    { q: "Can foreigners buy property in Dubai?", a: "Yes. Since 2002, foreign nationals can buy freehold property in designated areas of Dubai — Downtown Dubai, Dubai Marina, Palm Jumeirah, Business Bay, JVC, Emirates Hills, Arabian Ranches and many others. There are no restrictions on nationality, no residency requirement to purchase, and no personal income tax on rental income or capital gains." },
    { q: "What are the total costs when buying property in Dubai?", a: "Beyond the property price, expect: 4% Dubai Land Department (DLD) transfer fee, 2% agent commission, AED 4,000 DLD registration, AED 5,000 conveyance/oqood, roughly AED 3,000 in NOC fees, and a title-deed fee of AED 250. Point Penta consolidates these into a single closing statement before you sign." },
    { q: "How long does a Dubai property purchase take?", a: "Cash purchases close in 7–14 days after Form F is signed. Mortgage-backed purchases typically settle in 30–45 days once bank approval is in place." },
    { q: "Do I get UAE residency by buying property in Dubai?", a: "Yes. Property valued at AED 750,000+ qualifies for a 2-year investor visa. Property valued at AED 2 million+ qualifies for the 10-year Golden Visa. Both can include your spouse and children." },
    { q: "What is a fair rental yield in Dubai in 2026?", a: "Point Penta tracks gross yields ranging from 5.4% (Palm Jumeirah, prestige) to 7.8% (JVC, mid-market). Downtown Dubai averages 6.1%, Dubai Marina 6.8%, Business Bay 7.1%. Yields are refreshed daily on every community page." },
  ],
  rent: [
    { q: "How do I rent an apartment in Dubai?", a: "In Dubai the standard tenancy is one year, paid via 1–4 post-dated cheques. A 5% security deposit and 5% agent commission are typical. Ejari registration (approx AED 220) is required within 30 days of signing." },
    { q: "Can I rent without an Emirates ID?", a: "You need a valid UAE residency visa and Emirates ID to sign a rental contract in Dubai. If you're moving from abroad, Point Penta can arrange short-term serviced apartments while your residency is processed." },
    { q: "Are rents in Dubai negotiable?", a: "Yes. RERA's rental increase index (Dubai REST app) caps year-on-year increases, but new tenancies are open to negotiation — especially with a single-cheque payment or 6+ month advance." },
  ],
  "off-plan": [
    { q: "What is off-plan property in Dubai?", a: "Off-plan means buying directly from a developer before construction is complete. Payment plans typically spread 40–70% during construction and 30–60% on handover. Attractive for buyers seeking price appreciation, staged payments, and modern specifications." },
    { q: "Are Dubai off-plan payment plans safe?", a: "Yes, when you buy through RERA-registered escrow. Every AED you pay lands in a Central Bank supervised escrow that only releases to the developer against construction milestones certified by DLD. Point Penta only lists projects with active escrow." },
    { q: "Which are the top Dubai off-plan developers in 2026?", a: "Tier-1 masters: Emaar, DAMAC, Nakheel, Sobha, Meraas, Aldar, Select Group, Ellington, Dubai Properties. Point Penta partners directly with each and can secure launch-day pricing and payment plans." },
    { q: "Can I resell my off-plan unit before handover?", a: "Yes, once you've paid 30–40% of the price (varies by developer). Point Penta manages the entire assignment (NOC + secondary sale) end-to-end." },
  ],
  commercial: [
    { q: "Which are the best areas for commercial property in Dubai?", a: "DIFC and Business Bay are the top choices for financial and professional-services firms. Downtown Dubai for retail. JAFZA and Dubai South for logistics and light industrial." },
    { q: "Can foreigners own commercial property in Dubai?", a: "Yes, freehold ownership is available for commercial units in designated zones (DIFC, Business Bay, Downtown, etc.). For free-zone commercial licences, 100% foreign ownership applies with no local sponsor required since 2021." },
    { q: "What are typical office rents in Dubai?", a: "Grade-A DIFC offices trade at AED 250–450/sqft/year; Business Bay AED 90–180/sqft; JLT AED 70–140/sqft. Point Penta can source both fitted and shell-and-core inventory." },
  ],
};

const MODE_LABELS = {
  buy: { title: "Homes for sale", overline: "Buy", listing_type: "buy" },
  rent: { title: "Rental homes", overline: "Rent", listing_type: "rent" },
  "off-plan": { title: "Off-plan launches", overline: "Off-plan", listing_type: "off-plan" },
  commercial: { title: "Commercial mandates", overline: "Commercial", listing_type: "commercial" },
};

const SORTS = [
  { k: "newest", label: "Newest" },
  { k: "price_asc", label: "Price · low to high" },
  { k: "price_desc", label: "Price · high to low" },
  { k: "beds_desc", label: "Bedrooms · most first" },
  { k: "area_desc", label: "Area · largest first" },
];

const EMIRATES = ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain"];
const VIEWS = ["Sea", "Marina", "Burj Khalifa", "Skyline", "Golf", "Park", "Community", "Canal"];
const FURNISHINGS = ["Furnished", "Semi-furnished", "Unfurnished"];

export default function Properties({ mode = "buy" }) {
  const label = MODE_LABELS[mode] || MODE_LABELS.buy;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const [community, setCommunity] = useState(params.get("community") || "");
  const [emirate, setEmirate] = useState(params.get("emirate") || "");
  const [developerId, setDeveloperId] = useState(params.get("developer_id") || "");
  const [propertyType, setPropertyType] = useState(params.get("property_type") || "");
  const [bedsMin, setBedsMin] = useState(params.get("beds") || "");
  const [bathsMin, setBathsMin] = useState(params.get("baths") || "");
  const [priceMax, setPriceMax] = useState(params.get("price_max") || "");
  const [priceMin, setPriceMin] = useState(params.get("price_min") || "");
  const [areaMin, setAreaMin] = useState(params.get("area_min") || "");
  const [areaMax, setAreaMax] = useState(params.get("area_max") || "");
  const [furnishing, setFurnishing] = useState(params.get("furnishing") || "");
  const [view, setView] = useState(params.get("view") || "");
  const [q, setQ] = useState(params.get("q") || "");
  const [sort, setSort] = useState("newest");
  const [showAdv, setShowAdv] = useState(false);
  const [communities, setCommunities] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const saved = useLocalList(savedSearches);

  // Load all communities + developers to feed the dropdowns.
  useEffect(() => {
    (async () => {
      try {
        const [c, d] = await Promise.all([
          api.get("/communities", { params: { limit: 200 } }),
          api.get("/developers"),
        ]);
        setCommunities(c.data.items || []);
        setDevelopers(d.data.items || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const communityOptions = useMemo(() => {
    let list = communities;
    if (emirate) list = list.filter((c) => c.emirate === emirate);
    return [...new Set(list.map((c) => c.name).filter(Boolean))].sort();
  }, [communities, emirate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const query = {
        listing_type: label.listing_type,
        community: community || undefined,
        emirate: emirate || undefined,
        developer_id: developerId || undefined,
        property_type: propertyType || undefined,
        bedrooms_min: bedsMin || undefined,
        bathrooms_min: bathsMin || undefined,
        price_max: priceMax || undefined,
        price_min: priceMin || undefined,
        area_min: areaMin || undefined,
        area_max: areaMax || undefined,
        furnishing: furnishing || undefined,
        view: view || undefined,
        q: q || undefined,
      };
      const applySort = (list) => {
        if (sort === "price_asc") return [...list].sort((a, b) => a.price - b.price);
        if (sort === "price_desc") return [...list].sort((a, b) => b.price - a.price);
        if (sort === "beds_desc") return [...list].sort((a, b) => (b.bedrooms || 0) - (a.bedrooms || 0));
        if (sort === "area_desc") return [...list].sort((a, b) => (b.area_sqft || 0) - (a.area_sqft || 0));
        return list;
      };
      try {
        // Stale-while-revalidate — instant paint on repeat filter combos.
        const first = await cachedGet("/listings", { params: query });
        if (cancelled) return;
        setItems(applySort(first.data.items || []));
        setTotal(first.data.total || 0);
        setLoading(false);
        if (first.isStale) {
          const fresh = await first.refresh;
          if (!cancelled) {
            setItems(applySort(fresh.items || []));
            setTotal(fresh.total || 0);
          }
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, community, emirate, developerId, propertyType, bedsMin, bathsMin, priceMax, priceMin, areaMin, areaMax, furnishing, view, q, sort, label.listing_type]);

  function saveThis() {
    const name = window.prompt("Name this search:", `${label.overline}${community ? ` · ${community}` : ""}${bedsMin ? ` · ${bedsMin}+ bed` : ""}`);
    if (!name) return;
    savedSearches.save(name, { mode, community, emirate, developerId, propertyType, bedsMin, bathsMin, priceMax, priceMin, areaMin, areaMax, furnishing, view, q });
    toast.success("Search saved.");
  }
  function applySaved(s) {
    setCommunity(s.params.community || ""); setPropertyType(s.params.propertyType || "");
    setBedsMin(s.params.bedsMin || ""); setPriceMax(s.params.priceMax || "");
    setPriceMin(s.params.priceMin || ""); setQ(s.params.q || "");
    setEmirate(s.params.emirate || ""); setDeveloperId(s.params.developerId || "");
    setBathsMin(s.params.bathsMin || ""); setAreaMin(s.params.areaMin || "");
    setAreaMax(s.params.areaMax || ""); setFurnishing(s.params.furnishing || "");
    setView(s.params.view || "");
  }
  function clearAll() {
    setCommunity(""); setEmirate(""); setDeveloperId(""); setPropertyType("");
    setBedsMin(""); setBathsMin(""); setPriceMax(""); setPriceMin("");
    setAreaMin(""); setAreaMax(""); setFurnishing(""); setView(""); setQ("");
  }

  return (
    <div data-testid="properties-page">
      <SEO
        title={label.title + " in Dubai"}
        description={`Curated Point Penta ${label.overline.toLowerCase()} mandates across Dubai's most sought-after communities.`}
        path={mode === "buy" ? "/properties" : `/${mode}`}
        schema={[
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: label.overline, path: mode === "buy" ? "/properties" : `/${mode}` }]),
          faqSchema(MODE_FAQS[mode] || MODE_FAQS.buy),
        ]}
      />
      <section className="pt-6 pb-10 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: label.overline, path: mode === "buy" ? "/properties" : `/${mode}` }]} />
          <div className="overline text-[#001588] mb-4">{label.overline}</div>
          <h1 className="font-serif text-4xl lg:text-6xl leading-tight mb-8">{label.title}</h1>

          {mode === "rent" && (
            <div className="mb-8 border border-[#001588]/25 bg-[#F5F7F6] p-6" data-testid="rent-private-notice">
              <div className="text-xs tracking-[0.24em] uppercase text-[#001588] mb-2">Private inventory</div>
              <p className="text-neutral-800 text-lg font-medium mb-2">Rentals at Point Penta are matched privately.</p>
              <p className="text-neutral-700 text-sm max-w-2xl">
                We work exclusively with landlords who prefer curated introductions to open marketing. Share your
                brief with an advisor and we&apos;ll shortlist a set of rentals within 24 hours.
              </p>
              <div className="flex gap-3 mt-4">
                <Link to="/contact?interest=rent" className="px-5 py-2.5 bg-[#001588] text-white text-xs uppercase tracking-[0.2em]" data-testid="rent-contact-btn">Talk to a Point Penta advisor</Link>
                <a href="https://wa.me/971557396664?text=Hi%20Point%20Penta%2C%20I%27m%20looking%20for%20a%20rental" target="_blank" rel="noreferrer" className="px-5 py-2.5 border border-[#001588] text-[#001588] text-xs uppercase tracking-[0.2em]">WhatsApp</a>
              </div>
            </div>
          )}

          {/* ─── Redesigned filter bar ───────────────────────────────────
              Primary row: full-width search + Emirate + Community + Type + Beds + Price max
              Advanced panel: developer, baths, furnishing, view, price min, sqft, area max
              Active filters render as removable pills below.
          */}
          <div className="bg-white border border-neutral-200 p-4 lg:p-5 shadow-sm" data-testid="filter-panel">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2 lg:gap-3">
              <div className="md:col-span-6 flex items-center bg-[#ffffff] border border-transparent focus-within:border-[#001588]">
                <MagnifyingGlass size={18} className="mx-4 text-neutral-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Search by community, project, reference or developer…"
                       className="flex-1 bg-transparent py-3 text-[#050d19] placeholder:text-neutral-500 focus:outline-none pr-4"
                       data-testid="filter-search" />
              </div>
              <FilterSelect testId="filter-emirate" value={emirate} onChange={(v) => { setEmirate(v); setCommunity(""); }} label="Emirate">
                <option value="">All emirates</option>
                {EMIRATES.map((e) => <option key={e} value={e}>{e}</option>)}
              </FilterSelect>
              <FilterSelect testId="filter-community" value={community} onChange={setCommunity} label="Community">
                <option value="">All communities ({communityOptions.length})</option>
                {communityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </FilterSelect>
              <FilterSelect testId="filter-type" value={propertyType} onChange={setPropertyType} label="Type">
                <option value="">Any type</option>
                <option value="apartment">Apartment</option>
                <option value="villa">Villa</option>
                <option value="townhouse">Townhouse</option>
                <option value="penthouse">Penthouse</option>
                <option value="office">Office</option>
              </FilterSelect>
              <FilterSelect testId="filter-beds" value={bedsMin} onChange={setBedsMin} label="Bedrooms">
                <option value="">Any beds</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}+ beds</option>)}
              </FilterSelect>
              <FilterSelect testId="filter-baths" value={bathsMin} onChange={setBathsMin} label="Bathrooms">
                <option value="">Any baths</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}+ baths</option>)}
              </FilterSelect>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Max price (AED)</label>
                <input value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="e.g. 5000000"
                       type="number" className="w-full bg-white border border-neutral-200 px-3 py-[9px] text-sm text-[#050d19] placeholder:text-neutral-400 focus:outline-none focus:border-[#001588]"
                       data-testid="filter-price-max" />
              </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 pt-3 border-t border-neutral-100">
              <button onClick={() => setShowAdv(!showAdv)} className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-neutral-700 hover:text-[#001588]" data-testid="toggle-advanced">
                <SlidersHorizontal size={14} /> {showAdv ? "Hide" : "More filters"}
              </button>
              <button onClick={saveThis} className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-neutral-700 hover:text-[#001588]" data-testid="save-search-btn">
                <BookmarkSimple size={14} /> Save search
              </button>
              <button onClick={clearAll} className="text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-red-600" data-testid="clear-all-filters">
                Clear all
              </button>
              <div className="flex items-center gap-2 ml-auto text-xs uppercase tracking-[0.18em] text-neutral-600">
                <ArrowsDownUp size={14} />
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent focus:outline-none uppercase tracking-[0.18em]" data-testid="sort-select">
                  {SORTS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                </select>
              </div>
            </div>

            {/* Advanced (collapsible) */}
            {showAdv && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-neutral-100" data-testid="advanced-filters">
                <FilterSelect testId="filter-developer" value={developerId} onChange={setDeveloperId} label="Developer">
                  <option value="">All developers</option>
                  {developers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </FilterSelect>
                <FilterSelect testId="filter-furnishing" value={furnishing} onChange={setFurnishing} label="Furnishing">
                  <option value="">Any furnishing</option>
                  {FURNISHINGS.map((f) => <option key={f} value={f.toLowerCase()}>{f}</option>)}
                </FilterSelect>
                <FilterSelect testId="filter-view" value={view} onChange={setView} label="View">
                  <option value="">Any view</option>
                  {VIEWS.map((v) => <option key={v} value={v.toLowerCase()}>{v}</option>)}
                </FilterSelect>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Min price (AED)</label>
                  <input value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder="0" type="number" className="w-full bg-white border border-neutral-200 px-3 py-[9px] text-sm focus:outline-none focus:border-[#001588]" data-testid="filter-price-min" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Min area (sqft)</label>
                  <input value={areaMin} onChange={(e) => setAreaMin(e.target.value)} placeholder="0" type="number" className="w-full bg-white border border-neutral-200 px-3 py-[9px] text-sm focus:outline-none focus:border-[#001588]" data-testid="filter-area-min" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1 block">Max area (sqft)</label>
                  <input value={areaMax} onChange={(e) => setAreaMax(e.target.value)} placeholder="e.g. 5000" type="number" className="w-full bg-white border border-neutral-200 px-3 py-[9px] text-sm focus:outline-none focus:border-[#001588]" data-testid="filter-area-max" />
                </div>
              </div>
            )}

            {/* Active filter pills */}
            {(() => {
              const pills = [];
              if (emirate) pills.push({ label: `Emirate: ${emirate}`, clear: () => { setEmirate(""); setCommunity(""); } });
              if (community) pills.push({ label: community, clear: () => setCommunity("") });
              if (propertyType) pills.push({ label: propertyType, clear: () => setPropertyType("") });
              if (bedsMin) pills.push({ label: `${bedsMin}+ beds`, clear: () => setBedsMin("") });
              if (bathsMin) pills.push({ label: `${bathsMin}+ baths`, clear: () => setBathsMin("") });
              if (priceMin) pills.push({ label: `Min AED ${Number(priceMin).toLocaleString()}`, clear: () => setPriceMin("") });
              if (priceMax) pills.push({ label: `Max AED ${Number(priceMax).toLocaleString()}`, clear: () => setPriceMax("") });
              if (areaMin) pills.push({ label: `Min ${areaMin} sqft`, clear: () => setAreaMin("") });
              if (areaMax) pills.push({ label: `Max ${areaMax} sqft`, clear: () => setAreaMax("") });
              if (developerId) {
                const dev = developers.find((d) => d.id === developerId);
                if (dev) pills.push({ label: dev.name, clear: () => setDeveloperId("") });
              }
              if (furnishing) pills.push({ label: furnishing, clear: () => setFurnishing("") });
              if (view) pills.push({ label: `${view} view`, clear: () => setView("") });
              if (q) pills.push({ label: `“${q}”`, clear: () => setQ("") });
              if (pills.length === 0) return null;
              return (
                <div className="mt-4 pt-4 border-t border-neutral-100 flex flex-wrap items-center gap-2" data-testid="active-filters">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mr-1">Active:</span>
                  {pills.map((p, i) => (
                    <button key={i} onClick={p.clear} className="inline-flex items-center gap-1.5 bg-[#001588] text-white text-xs px-3 py-1 hover:bg-black">
                      {p.label} <span className="opacity-70">×</span>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>

          {saved.length > 0 && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-neutral-500">Saved:</span>
              {saved.slice(0, 6).map((s) => (
                <button key={s.id} onClick={() => applySaved(s)} className="text-xs bg-neutral-50 border border-neutral-200 px-3 py-1 hover:border-[#001588]" data-testid={`saved-search-${s.id}`}>
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-sm text-neutral-500 mb-8" data-testid="results-count">
            {loading ? "Loading…" : `${total} mandate${total === 1 ? "" : "s"} matching`}
          </div>
          {items.length === 0 && !loading ? (
            <div className="border border-neutral-200 p-16 text-center text-neutral-500">
              No matching mandates. Adjust your filters or contact us — we hold many off-market briefs.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {items.map((p) => <PropertyCard key={p.id} p={p} />)}
            </div>
          )}

          <RecentlyViewed />
        </div>
      </section>

      {/* Visible FAQ block — mirrors the FAQ schema so Google AI Overviews
          and Perplexity can quote Point Penta answers directly. */}
      <section className="py-16 border-t border-neutral-200 bg-[#F5F7F6]">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <div className="overline text-[#001588] mb-3">Frequently asked</div>
          <h2 className="font-serif text-3xl sm:text-4xl mb-10">{label.overline} in Dubai — 2026 buyer questions</h2>
          <div className="divide-y divide-neutral-300">
            {(MODE_FAQS[mode] || MODE_FAQS.buy).map((f, i) => (
              <details key={i} className="py-5 group" data-testid={`mode-faq-${i}`}>
                <summary className="cursor-pointer flex justify-between items-start gap-4 list-none">
                  <h3 className="text-base sm:text-lg font-medium text-[#050d19] group-open:text-[#001588]">{f.q}</h3>
                  <span className="text-[#001588] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm sm:text-base text-neutral-700 leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}


function FilterSelect({ label, value, onChange, children, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1 block">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-white border border-neutral-200 px-3 py-[9px] text-sm text-[#050d19] focus:outline-none focus:border-[#001588] cursor-pointer"
      >
        {children}
      </select>
    </div>
  );
}

function RecentlyViewed() {
  const ids = useLocalList(recentlyViewed);
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!ids.length) { setItems([]); return; }
    Promise.all(ids.slice(0, 6).map((id) => api.get(`/listings/${id}`).then((r) => r.data).catch(() => null)))
      .then((r) => setItems(r.filter(Boolean)));
  }, [ids.join(",")]);
  if (items.length === 0) return null;
  return (
    <div className="mt-20 pt-14 border-t border-neutral-200" data-testid="recently-viewed">
      <h2 className="font-serif text-3xl mb-8">Recently viewed</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {items.map((p) => (
          <Link key={p.id} to={`/properties/${p.id}`} className="pp-hover-zoom aspect-[4/3] block relative">
            {p.images?.[0] && <img src={p.images[0].startsWith("http") ? p.images[0] : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.images[0]}`} alt={p.title || "Recently viewed property"} className="w-full h-full object-cover" loading="lazy" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
            <div className="absolute bottom-2 left-2 right-2 text-xs text-white truncate">{p.title}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}
